















import {
  computed,
  defineComponent,
  onUnmounted,
  ref,
  watch,
  onMounted
} from '@vue/composition-api'
import Postmate from 'postmate'
import config from '@/config'
import firebase from 'firebase/compat/app'
import { currencyCode, currencyCode } from '@/util/currency_helper'
import SubmitFeatureFeedback from '@/pmd/pages/settings/GhlLabs/SubmitFeatureFeedback.vue'

export default defineComponent({
  name: 'PaymentsApp',
  components: {
    SubmitFeatureFeedback
  },
  setup(props, ctx) {
    const childApp = ref<any>(null)
    const paymentsAppRef = ref<any>()
    const routeName = computed(() => ctx.root.$route.name)
    const openSuggestionPanel = ref(false)
    const featureKey = ref('payments')

    function getToken() {
      return firebase.auth().currentUser.getIdToken()
    }

    const user = computed(() => {
      return ctx.root.$store.state.user.user
    })

    const location = computed(() => {
      return ctx.root.$store.state.locations.currentLocation
    })

    const currency = computed(() => location.value?.country ? currencyCode[location.value?.country] : 'USD')

    const sidebarVersion = computed(() => {
      return ctx.root.$store.getters['sidebarv2/getVersion']
    })

    const hasUnsavedChanges = ref<boolean>(false)

    onMounted(() => {
      window.addEventListener("beforeunload", (e) => {
        if (hasUnsavedChanges.value) {
          // Cancel the event
          e.preventDefault(); // If you prevent default behavior in Mozilla Firefox prompt will always be shown
          // Chrome requires returnValue to be set
          e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        }
      });
      loadMicroApp()
    })

    async function loadMicroApp() {
      if (!routeName) return
      const userId = user.value.id
      const userEmail = user.value.email
      const userPermissions = user.value.permissions
      let locale = 'en-US'
      if (location.value.locale_settings?.platform) {
        locale = location.value.locale_settings.platform.replace('_', '-')
      }
      const params = {
        userId,
        userEmail,
        tokenId: await getToken(),
        altType: 'location',
        altId: location.value.id,
        currency: currency.value,
        timezone: location.value.timezone,
        companyId: location.value.company_id,
        userPermissions,
        locale
      }
      if(process.env.NODE_ENV === 'development') {
        Postmate.debug = false
      }
      const appURL = `${config.paymentsAppUrl}${ctx.root.$route.fullPath.replace('v2/','')}`
      const handshake = new Postmate({
        container: paymentsAppRef.value as HTMLElement,
        url: appURL,
        name: 'payment-app',
        model: params,
      })

      if (handshake) {
        handshake.then((child: any) => {
          child.on('spm-ts', (data: any) => {
            const route =  sidebarVersion.value === 'v2' ? `/v2${data.router}` : data.router
            if (data.newTab || data.forceReload) {
              let routeUrl = ctx.root.$router.resolve(route)
              if (data.forceReload) {
                window.location.href = routeUrl.href
              } else {
                window.open(routeUrl.href, '_blank')
              }
            } else {
              ctx.root.$router.push(route)
            }
          })
          child.on('refresh-token', async () => {
            child.call('updated-token', await getToken())
          })
          child.on('copy-to-clipboard', async (data: any) => {
            try {
              await navigator.clipboard.writeText(data.text)
              child.call('copied-to-clipboard', {
                ...data,
                success: true
              })
            } catch (e) {
              child.call('copied-to-clipboard', {
                ...data,
                success: false
              })
            }
          })
          child.on('set-unsaved-changed', (val: boolean) => {
            hasUnsavedChanges.value = val
          })
          child.on('send-feedback',(feature: string) => {
            openSuggestionPanel.value = true
            if (feature) featureKey.value = `payments.${feature}`
          })
        })
        .catch(async (err: Error) => {
          console.error({ handledError: err })
          paymentsAppRef.value.innerHTML = ''
          await loadMicroApp()
        })
        childApp.value = handshake
      } else {
        alert('Something went wrong. Please try again.')
      }
    }

    function destroyApp() {
      return new Promise((resolve: any) => {
        if (childApp.value) {
          childApp.value.then((child: any) => {
            child.destroy()
            childApp.value = null
          })
        }
        resolve()
      })
    }

    async function reloadApp() {
      await destroyApp()
      await loadMicroApp()
    }

    onUnmounted(() => {
      destroyApp()
    })

    watch(routeName, reloadApp)

    return {
      paymentsAppRef,
      reloadApp,
      openSuggestionPanel,
      featureKey,
      location
    }
  },
  watch:{
    '$route.params.location_id':function () {
      this.reloadApp()
    }
  }
})
